import { DotsThree } from "@phosphor-icons/react";
import { Link } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { actionsToMenuItems } from "~/actions/adapters/menu";
import { useActionContext } from "~/actions/context";
import { documentRowActions } from "~/actions/definitions/documents";
import type { Collection, Document } from "~/api/types";
import { IconButton } from "~/foundation/components/IconButton";
import { ContextMenuSurface, Menu } from "~/foundation/components/Menu";
import { formatRelativeTime } from "~/foundation/utils/time";

export interface DocumentRowProps {
  document: Document;
  /** The document's collection, for the meta line. Omitted where it would be redundant. */
  collection?: Collection | undefined;
}

/**
 * One document in a list, styled after the platform's own list rows: a borderless rounded
 * row with a 15px medium title, a muted 13px meta line (time ago · collection) beneath it,
 * a quiet hover background, and actions that stay out of the way until pointed at.
 *
 * The overflow menu and the right-click menu are built from the same `documentRowActions`,
 * gated by the same server-supplied abilities — one declaration reaches every surface.
 */
export function DocumentRow({ document, collection }: DocumentRowProps) {
  const { t, i18n } = useTranslation();
  const context = useActionContext({ source: "contextmenu", activeEntityId: document.id });
  const menuItems = actionsToMenuItems(documentRowActions, context);

  const meta = [
    t("Updated {{when}}", { when: formatRelativeTime(document.updatedAt, i18n.language) }),
    ...(collection ? [collection.name] : []),
  ].join(" · ");

  return (
    <ContextMenuSurface items={menuItems} ariaLabel={t("Document actions")}>
      <li className="group relative flex items-center gap-2 rounded-md px-3 py-2.5 transition-colors duration-100 ease-in-out hover:bg-hover-bg motion-reduce:transition-none">
        <Link
          to="/documents/$documentId"
          params={{ documentId: document.id }}
          className="min-w-0 flex-1"
        >
          <span className="flex items-baseline gap-2">
            {document.emoji ? (
              <span aria-hidden className="shrink-0">
                {document.emoji}
              </span>
            ) : null}
            <span className="truncate text-body font-medium text-text">{document.title}</span>
          </span>
          <span className="mt-0.5 block truncate text-small text-text-tertiary">{meta}</span>
        </Link>
        {menuItems.length > 0 ? (
          <div className="shrink-0 transition-opacity duration-100 sm:opacity-0 sm:group-focus-within:opacity-100 sm:group-hover:opacity-100 sm:has-[[data-state=open]]:opacity-100 motion-reduce:transition-none">
            <Menu
              align="end"
              ariaLabel={t("Document actions")}
              items={menuItems}
              trigger={<IconButton icon={DotsThree} size="sm" aria-label={t("Document actions")} />}
            />
          </div>
        ) : null}
      </li>
    </ContextMenuSurface>
  );
}
